import { FC } from 'react';
import { Box, Stack, Theme, Typography, useMediaQuery, useTheme } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { useAuth } from 'react-oidc-context';

import { Admin, Billing, Director, Dispatch, Lead, Manager } from 'domain/types/Roles';
import { useCaseCheckAccess } from 'application/auth/useCases/useCaseCheckAccess';
import { Toolbar } from 'targets/web/components/Toolbar';
import IconButton from 'targets/web/components/IconButton';
import { PrimeFlightIcon } from 'targets/web/components/Icons';
import { useNavigationDrawer } from 'targets/web/modules/dashboard/hooks/useNavigationDrawer';
import { LogoutButton, OfflineModeBanner } from 'targets/web/modules/dashboard/components';

const COMPOSITE_ROLES = [Admin, Director, Manager, Dispatch, Lead, Billing] as const;

const roleLabel: Record<string, string> = {
  [Admin]: 'Admin',
  [Director]: 'Director',
  [Manager]: 'Manager',
  [Dispatch]: 'Dispatch',
  [Lead]: 'Lead',
  [Billing]: 'Billing',
};

export const DashboardToolbar: FC = () => {
  const isDesktop = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'));
  const theme = useTheme();
  const { toggle } = useNavigationDrawer();
  const auth = useAuth();
  const { getAllRoles } = useCaseCheckAccess();

  const profile = auth.user?.profile;
  const roles = getAllRoles();
  const displayRole = COMPOSITE_ROLES.find((r) => roles.includes(r));
  const displayName = (profile?.given_name ?? profile?.name ?? '') as string;

  return (
    <>
      <Toolbar sx={{ zIndex: isDesktop ? theme.zIndex.drawer + 1 : undefined }}>
        {!isDesktop && (
          <IconButton variant="text" size="medium" onClick={() => toggle()}>
            <MenuIcon />
          </IconButton>
        )}
        <PrimeFlightIcon />
        <Box sx={{ flexGrow: 1 }} />
        {displayName && (
          <Stack alignItems="flex-end" sx={{ mr: 2 }}>
            <Typography variant="body2" color="text.primary" fontWeight={500} lineHeight={1.2}>
              {displayName}
            </Typography>
            {displayRole && (
              <Typography variant="caption" color="text.disabled" lineHeight={1.2}>
                {roleLabel[displayRole]}
              </Typography>
            )}
          </Stack>
        )}
        <LogoutButton />
      </Toolbar>
      <OfflineModeBanner />
    </>
  );
};
